import type { HTTPError } from './HTTPError';

export const REGISTRATION_DENIED_MESSAGE =
    'Your registration could not be completed. Please contact support for more information.';

/**
 * Return true if an error is either a permissions denied from Firestore or the RTDB.
 *
 * Taken from the implementation in firebase packages
 * https://github.com/firebase/firebase-js-sdk/blob/master/packages/rules-unit-testing/src/api/index.ts#L692
 */
export function isFirebasePermissionsDenied(
    error: (Error & { code?: string }) | HTTPError
): boolean {
    const errCode = error?.code?.toString().toLowerCase() || '';
    const errMessage = error?.message?.toLowerCase() || '';
    return (
        errCode === 'permission-denied' ||
        errCode === 'permission_denied' ||
        errMessage.indexOf('permission_denied') >= 0 ||
        errMessage.indexOf('permission denied') >= 0
    );
}

/**
 * Normal Firebase auth errors that should not be captured by Sentry and will not be thrown to the caller.
 *
 * https://firebase.google.com/docs/reference/js/firebase.auth.Auth#signInWithEmailAndPassword
 * https://firebase.google.com/docs/reference/js/firebase.auth.Auth#createUserWithEmailAndPassword
 * https://firebase.google.com/docs/reference/js/firebase.auth.Error
 * https://github.com/firebase/firebase-js-sdk/blob/master/packages/auth/src/core/errors.ts
 */
export const NORMAL_AUTH_ERRORS = [
    'auth/network-request-failed',
    'auth/too-many-requests',
    'auth/quota-exceeded',
    'auth/user-disabled',
    'auth/popup-closed-by-user',
    'auth/redirect-cancelled-by-user',
    'auth/cancelled-popup-request',
    'auth/user-cancelled',
    'auth/credential-already-in-use',
    // For sign-up
    'auth/email-already-in-use',
    'auth/weak-password',
    'auth/account-exists-with-different-credential',
    // For login
    'auth/user-mismatch',
    'auth/invalid-email',
    'auth/user-not-found',
    'auth/wrong-password',
    'auth/missing-password',
    // For apply of action code
    'auth/expired-action-code',
    'auth/invalid-action-code',
    // Phone authentication
    'auth/code-expired',
    'auth/invalid-phone-number',
    // For MFA verification
    'auth/unverified-email',

    // Errors the should be handled by the caller, and should not be included in this list:
    // 'auth/invalid-verification-code',
];

/**
 * Check if an error is a normal Firebase auth error.
 */
export function isNormalAuthError(error: unknown): error is Error & { code: string } {
    return (
        error instanceof Error &&
        'code' in error &&
        typeof (error as { code?: string }).code === 'string' &&
        (NORMAL_AUTH_ERRORS.includes((error as { code: string }).code) ||
            ((error as { code: string }).code === 'auth/internal-error' &&
                isRegistrationDeniedError(error)))
    );
}

/** Recognize the deliberate registration denial, including Firebase's wrapped response. */
export function isRegistrationDeniedError(error: unknown): boolean {
    if (!(error instanceof Error) || !('code' in error)) {
        return false;
    }
    if (error.code === 403) {
        return (
            'status' in error &&
            error.status === 'PERMISSION_DENIED' &&
            error.message === REGISTRATION_DENIED_MESSAGE
        );
    }
    if (error.code !== 'auth/internal-error') {
        return false;
    }
    try {
        const response = JSON.parse(error.message.match(/\{.*\}/s)?.[0] ?? 'null');
        return (
            response?.error?.code === 403 &&
            response.error.status === 'PERMISSION_DENIED' &&
            response.error.message === REGISTRATION_DENIED_MESSAGE
        );
    } catch {
        return false;
    }
}
